import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Box, AppBar, Toolbar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import StudentList from '../pubLab/StudentList';

function LabConsolleManageUsersPage(props) {
    const { labProps, isAdmin } = props;
    const [isLoading, setIsLoading] = useState(0);
    const [students, setStudents] = useState(labProps?.students || []);
    const [teachers, setTeachers] = useState(labProps?.teachers || []);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusRequest, setStatusRequest] = useState(null);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const originalStudentsRef = useRef(JSON.stringify(labProps?.students || []));
    const originalTeachersRef = useRef(JSON.stringify(labProps?.teachers || []));

    // Sincronizza gli stati quando labProps cambia
    useEffect(() => {
        if (labProps?.students) {
            setStudents(labProps.students);
            originalStudentsRef.current = JSON.stringify(labProps.students);
        }
        if (labProps?.teachers) {
            setTeachers(labProps.teachers);
            originalTeachersRef.current = JSON.stringify(labProps.teachers);
        }
        setHasUnsavedChanges(false);
    }, [labProps]);

    // Monitora i cambiamenti in studenti e insegnanti
    useEffect(() => {
        const studentsChanged = JSON.stringify(students) !== originalStudentsRef.current;
        const teachersChanged = JSON.stringify(teachers) !== originalTeachersRef.current;
        setHasUnsavedChanges(studentsChanged || teachersChanged);
    }, [students, teachers]);

    const handleBack = () => {
        console.log('handleBack - hasUnsavedChanges:', hasUnsavedChanges);
        console.log('Current students:', JSON.stringify(students));
        console.log('Original students:', originalStudentsRef.current);
        console.log('Current teachers:', JSON.stringify(teachers));
        console.log('Original teachers:', originalTeachersRef.current);

        if (hasUnsavedChanges) {
            setShowUnsavedDialog(true);
        } else {
            props.setIsShowManageUsersLab(false);
        }
    };

    const handleConfirmBack = () => {
        setShowUnsavedDialog(false);
        props.setIsShowManageUsersLab(false);
    };

    const handleCancelBack = () => {
        setShowUnsavedDialog(false);
    };

    const handleSave = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        setIsLoading(1);

        try {
            const url = process.env.REACT_APP_URL_UPD_STD_TEACHERS;

            const payload = {
                idLab: labProps.idLab,
                email: props.apiAuthResponse,
                students: students,
                teachers: teachers
            };

            const formBody = [];
            formBody.push("payload=" + encodeURIComponent(JSON.stringify(payload)));

            const response = await fetch(url, {
                credentials: 'include',
                method: "POST",
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: formBody
            });

            const data = await response.json();
            setStatusRequest(data);

            // Se il salvataggio ha successo, aggiorna i riferimenti originali
            if (data.statusRequest === 'success' || data.status === 'success') {
                originalStudentsRef.current = JSON.stringify(students);
                originalTeachersRef.current = JSON.stringify(teachers);
                setHasUnsavedChanges(false);
            }
        } catch (error) {
            console.log(error, "Errore nell'aggiornamento utenti!");
            setStatusRequest({ status: 'error', message: 'Errore nell\'aggiornamento degli utenti' });
        } finally {
            setIsLoading(0);
            setTimeout(() => setIsSubmitting(false), 500);
        }
    };

    if (!labProps) {
        return (
            <AppBar position="sticky" color="default" elevation={1} sx={{ backgroundColor: '#fff' }}>
                <Toolbar>
                    <IconButton edge="start" onClick={() => props.setIsShowManageUsersLab(false)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#a71e3b' }}>
                        Gestione Docenti e Studenti
                    </Typography>
                </Toolbar>
            </AppBar>
        );
    }

    return (
        <React.Fragment>
            <AppBar position="sticky" color="default" elevation={1} sx={{ backgroundColor: '#fff' }}>
                <Toolbar sx={{ gap: 2 }}>
                    <IconButton edge="start" onClick={handleBack} disabled={isSubmitting}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', color: '#a71e3b' }}>
                            Gestione Docenti e Studenti
                        </Typography>
                        <Typography variant="body2" color="textSecondary" noWrap>
                            {labProps.labName} &mdash; {labProps.baseImage?.name}
                        </Typography>
                    </Box>
                    {statusRequest && (
                        <Typography
                            variant="body2"
                            color={statusRequest.statusRequest === 'success' ? 'success.main' : 'error'}
                            sx={{ flexShrink: 0 }}
                        >
                            {statusRequest.statusRequest === 'success'
                                ? 'Utenti aggiornati con successo!'
                                : statusRequest.message || 'Errore nell\'aggiornamento degli utenti'}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        disabled={isSubmitting}
                        onClick={handleSave}
                    >
                        {isSubmitting ? 'Salvando...' : 'Salva modifiche'}
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 3 }}>
                <StudentList
                    status={labProps.status}
                    labType={labProps.labType}
                    labVersion={labProps.labVersion}
                    isAdmin={isAdmin}
                    apiAuthResponse={props.apiAuthResponse}
                    isLoading={isLoading}
                    students={students}
                    teachers={teachers}
                    setStudents={setStudents}
                    setTeachers={setTeachers}
                />
            </Box>

            {/* Dialog di conferma per modifiche non salvate */}
            <Dialog
                open={showUnsavedDialog}
                onClose={handleCancelBack}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Modifiche non salvate</DialogTitle>
                <DialogContent>
                    <Typography>
                        Hai effettuato modifiche che non sono state salvate.
                        Se esci senza salvare, le modifiche andranno perse.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelBack}
                        color="primary"
                        variant="outlined"
                    >
                        Continua a modificare
                    </Button>
                    <Button
                        onClick={handleConfirmBack}
                        color="error"
                        variant="contained"
                    >
                        Abbandona modifiche
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export default LabConsolleManageUsersPage;
